import { ROUTE_PATHS } from '@/constants';
import type { AuthData } from '@/types/auth';
import type { QueryClient } from '@tanstack/react-query';
import {
  type AnyRouter,
  ErrorComponent,
  type NavigateFn,
  createBrowserHistory,
  createRouter,
} from '@tanstack/react-router';
import React from 'react';

import { routeTree } from '../routeTree.gen';

import { clearClientSession } from './clear-client-session';
import { canNavigateTo } from './navigation-guards';
import { queryClient } from './query-client';
import { setUnauthorizedHandler } from './unauthorized-handler';

export type RouterContext = {
  authentication: AuthData;
  queryClient: QueryClient;
};

// Create the history instance first
const history = createBrowserHistory();

// Timestamp marking app start (used to suppress immediate /login navigations
// during a post-login redirect where the session cookie may still be propagating)
const appStartTs = Date.now();

// Create a NavigationManager that we'll use before router is initialized
class NavigationManager {
  private static instance: NavigationManager;
  private routerInstance: typeof router | null = null;
  private navigationInFlight = false;

  private constructor() {}

  static getInstance(): NavigationManager {
    if (!NavigationManager.instance) {
      NavigationManager.instance = new NavigationManager();
    }
    return NavigationManager.instance;
  }

  setRouter(r: typeof router) {
    this.routerInstance = r;
  }

  navigate(to: string, search?: Record<string, string>) {
    const searchParams = search ? `?${new URLSearchParams(search).toString()}` : '';
    const fullPath = `${to}${searchParams}`;

    // If we're in a post-login callback (URL contains login=success) and the
    // app just started, suppress navigations to /login for a short window to
    // allow the session cookie to propagate and avoid bouncing back to log in.
    try {
      if (typeof window !== 'undefined' && window.location.search.includes('login=success')) {
        const now = Date.now();
        const POST_LOGIN_SUPPRESS_MS = 2000;
        if (to === ROUTE_PATHS.LOGIN && now - appStartTs < POST_LOGIN_SUPPRESS_MS) {
          // Suppress immediate /login navigation during post-login callback without logging.
          return;
        }
      }
    } catch {}

    // Guard against rapid repeated navigations.
    const guardKey = `${to}|${JSON.stringify({ params: undefined, search })}`;
    if (typeof window !== 'undefined' && !canNavigateTo(guardKey)) {
      // Navigation suppressed by guard — no logging.
      return;
    }

    if (this.navigationInFlight) {
      // Suppress overlapping navigation without console output.
      return;
    }

    this.navigationInFlight = true;
    try {
      if (this.routerInstance) {
        // Use the router's history API which doesn't have the strict typing
        this.routerInstance.history.push(fullPath);
      } else {
        // Fallback if router isn't initialized yet
        window.location.href = fullPath;
      }
    } finally {
      // Allow subsequent navigations after a microtask so quick duplicates are suppressed
      setTimeout(() => {
        this.navigationInFlight = false;
      }, 0);
    }
  }
}

const navigationManager = NavigationManager.getInstance();

// Define handleUnauthorized locally and register it globally
const handleUnauthorized = () => {
  if (window.location.pathname !== ROUTE_PATHS.LOGIN && window.location.pathname !== ROUTE_PATHS.RESET_PASSWORD) {
    // Defer to avoid synchronous state updates during React commit.
    // canNavigateTo is NOT called here — NavigationManager.navigate calls it
    // internally, and a redundant outer call would consume the sessionStorage
    // token, causing the inner check to return false and silently drop the
    // redirect within the 1000ms guard window.
    setTimeout(() => {
      // Clear all client-side session data before redirecting so the user
      // starts fresh on the login page (same as an explicit logout).
      clearClientSession();
      navigationManager.navigate(ROUTE_PATHS.LOGIN, {
        redirect: window.location.pathname + window.location.search,
      });
    }, 50);
  }
};

// Register handler with the unauthorized-handler module (used by api-client, useAuthStatus, and __root)
setUnauthorizedHandler(handleUnauthorized);

// Create the router with the queryClient
const router = createRouter({
  context: {
    authentication: null as unknown as AuthData,
    queryClient,
  },
  defaultErrorComponent: ({ error }) => <ErrorComponent error={error} />,
  defaultPreload: 'intent',
  defaultStaleTime: 5000,
  defaultPreloadStaleTime: 0,
  defaultStructuralSharing: true,
  defaultViewTransition: true,
  history,
  routeTree,
  scrollRestoration: true,
  scrollRestorationBehavior: 'instant',
  scrollToTopSelectors: ['[data-scroll-restoration-id="main-scrollable-area"]'],
});

// Defensive wrapper for router.navigate to suppress rapid repeated navigations.
// Some parts of the app may attempt to navigate concurrently (e.g. api-client
// unauthorized handler + auth hooks + route beforeLoad). Wrapping navigate here
// ensures a central guard (canNavigateTo) is consulted before making a transition
// which reduces the chance of nested transitions that lead to React's maximum
// update depth exceeded errors.
{
  const typedRouter = router as AnyRouter & { navigate: NavigateFn };
  const origNavigate: NavigateFn = typedRouter.navigate.bind(typedRouter);

  let navigationInFlight = false;

  typedRouter.navigate = async opts => {
    try {
      let targetPath = '';
      let guardKey = '';
      if (opts && typeof opts === 'object') {
        const { to, params, search } = opts as {
          to?: string;
          params?: unknown;
          search?: unknown;
        };
        targetPath = to ?? '';
        guardKey = `${to ?? ''}|${JSON.stringify({ params, search })}`;
      }

      if (navigationInFlight) {
        return Promise.resolve();
      }

      const keyForGuard = guardKey || targetPath;
      if (typeof window !== 'undefined' && keyForGuard && !canNavigateTo(keyForGuard)) {
        return Promise.resolve();
      }

      navigationInFlight = true;
      return await new Promise((resolve, reject) => {
        setTimeout(() => {
          try {
            const result = origNavigate(opts);
            if (result && typeof result.then === 'function') {
              result.then(
                val => {
                  navigationInFlight = false;
                  return resolve(val);
                },
                err => {
                  navigationInFlight = false;
                  return reject(err);
                },
              );
            } else {
              navigationInFlight = false;
              resolve(result);
            }
          } catch (err) {
            navigationInFlight = false;
            reject(err);
          }
        }, 0);
      });
    } catch {
      return origNavigate(opts);
    }
  };
}

// Set the router instance in our navigation manager
navigationManager.setRouter(router);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export { router };
